/**
 * DuplicateUploadDialog — shown when an upload matches an existing document.
 *
 * The backend returns HTTP 409 with a duplicate signal when the uploaded file's
 * content (or name) already exists. This dialog surfaces that to the user and
 * offers three resolutions:
 *   - Update  -> re-use/refresh the existing document (no new entry)
 *   - Copy    -> upload as a separate, distinct document
 *   - Cancel  -> do nothing
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

/** What kind of duplicate the backend detected. */
export type DuplicateKind = 'content' | 'name' | 'content_and_name' | string;

/** The user's chosen resolution. */
export type DuplicateResolution = 'update' | 'copy';

export interface DuplicateInfo {
  duplicateKind: DuplicateKind;
  existingDocumentId?: string | null;
  existingTitle?: string | null;
  /** True when the matching document is owned by ANOTHER user (shared with you),
   *  not your own document -> a distinct message. */
  ownedByOther?: boolean;
}

interface DuplicateUploadDialogProps {
  open: boolean;
  info: DuplicateInfo | null;
  onResolve: (resolution: DuplicateResolution) => void;
  onCancel: () => void;
}

export const DuplicateUploadDialog: React.FC<DuplicateUploadDialogProps> = ({
  open,
  info,
  onResolve,
  onCancel,
}) => {
  const { t } = useTranslation();

  const bothMatch = info?.duplicateKind === 'content_and_name';
  const byName = info?.duplicateKind === 'name';
  const byOther = Boolean(info?.ownedByOther);
  const name = info?.existingTitle ?? '';
  const hasName = name.length > 0;

  // Cases: content / name / content_and_name, each x your-own / shared-by-other.
  const title = bothMatch
    ? t('documents.duplicate.titleBoth', {
        defaultValue: 'Identisches Dokument (Inhalt und Name) bereits vorhanden',
      })
    : byName
      ? t('documents.duplicate.titleName', { defaultValue: 'Dokument mit gleichem Namen vorhanden' })
      : t('documents.duplicate.titleContent', {
          defaultValue: 'Dokument mit denselben Daten bereits vorhanden',
        });

  let message: string;
  if (bothMatch && byOther) {
    message = hasName
      ? t('documents.duplicate.messageBothOther', {
          defaultValue:
            'Ein anderer Benutzer hat bereits ein identisches Dokument ("{{name}}") mit demselben Inhalt UND Namen. Da es Ihnen nicht gehört, können Sie nur eine eigene Kopie hochladen.',
          name,
        })
      : t('documents.duplicate.messageBothOtherNoName', {
          defaultValue:
            'Ein anderer Benutzer hat bereits ein identisches Dokument mit demselben Inhalt UND Namen. Da es Ihnen nicht gehört, können Sie nur eine eigene Kopie hochladen.',
        });
  } else if (bothMatch) {
    message = hasName
      ? t('documents.duplicate.messageBoth', {
          defaultValue:
            'Dieses Dokument ist bereits vorhanden — sowohl der Inhalt als auch der Name ("{{name}}") stimmen überein. Möchten Sie das bestehende aktualisieren oder eine Kopie hochladen?',
          name,
        })
      : t('documents.duplicate.messageBothNoName', {
          defaultValue:
            'Dieses Dokument ist bereits vorhanden — sowohl der Inhalt als auch der Name stimmen überein. Möchten Sie das bestehende aktualisieren oder eine Kopie hochladen?',
        });
  } else if (byName && byOther) {
    message = hasName
      ? t('documents.duplicate.messageNameOther', {
          defaultValue:
            'Ein anderer Benutzer hat bereits ein Dokument mit demselben Namen ("{{name}}"). Da es Ihnen nicht gehört, können Sie nur eine eigene Kopie hochladen.',
          name,
        })
      : t('documents.duplicate.messageNameOtherNoName', {
          defaultValue:
            'Ein anderer Benutzer hat bereits ein Dokument mit demselben Namen. Da es Ihnen nicht gehört, können Sie nur eine eigene Kopie hochladen.',
        });
  } else if (byName) {
    message = hasName
      ? t('documents.duplicate.messageName', {
          defaultValue:
            'Ein Dokument mit demselben Namen ist bereits vorhanden ("{{name}}"). Möchten Sie das bestehende Dokument aktualisieren oder eine Kopie hochladen?',
          name,
        })
      : t('documents.duplicate.messageNameNoName', {
          defaultValue:
            'Ein Dokument mit demselben Namen ist bereits vorhanden. Möchten Sie das bestehende Dokument aktualisieren oder eine Kopie hochladen?',
        });
  } else if (byOther) {
    message = hasName
      ? t('documents.duplicate.messageContentOther', {
          defaultValue:
            'Ein anderer Benutzer hat bereits ein inhaltlich identisches Dokument ("{{name}}"). Da es Ihnen nicht gehört, können Sie nur eine eigene Kopie hochladen.',
          name,
        })
      : t('documents.duplicate.messageContentOtherNoName', {
          defaultValue:
            'Ein anderer Benutzer hat bereits ein inhaltlich identisches Dokument. Da es Ihnen nicht gehört, können Sie nur eine eigene Kopie hochladen.',
        });
  } else {
    message = hasName
      ? t('documents.duplicate.messageContent', {
          defaultValue:
            'Ein inhaltlich identisches Dokument ist bereits vorhanden ("{{name}}"). Möchten Sie das bestehende Dokument aktualisieren oder eine Kopie hochladen?',
          name,
        })
      : t('documents.duplicate.messageContentNoName', {
          defaultValue:
            'Ein inhaltlich identisches Dokument ist bereits vorhanden. Möchten Sie das bestehende Dokument aktualisieren oder eine Kopie hochladen?',
        });
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoOutlinedIcon sx={{ color: '#003064' }} />
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: '#333' }}>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onCancel} color="inherit">
          {t('common.cancel', { defaultValue: 'Abbrechen' })}
        </Button>
        {/* "Update" is hidden when the match is owned by ANOTHER user -- you
            cannot update a document you do not own; only upload your own copy. */}
        {!byOther && (
          <Button onClick={() => onResolve('update')} variant="outlined" sx={{ color: '#003064', borderColor: '#003064' }}>
            {t('documents.duplicate.update', { defaultValue: 'Aktualisieren' })}
          </Button>
        )}
        <Button
          onClick={() => onResolve('copy')}
          variant="contained"
          sx={{ backgroundColor: '#003064', '&:hover': { backgroundColor: '#00204d' } }}
        >
          {t('documents.duplicate.copy', { defaultValue: 'Kopie hochladen' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
